import * as babylon from "@babylonjs/core";

import type { FileRecord } from "@/shared/domain/files";
import {
  readVisualizationAsset,
  sourceFileIdOfAsset,
  type VisualizationAssetRecord,
} from "@/shared/domain/urdf";

import type { ModelFileService } from "./ModelLoader";

/**
 * How a converted asset's axes were baked: which glTF axis holds the robot's
 * up, and which its forward.
 *
 * The scene draws every model facing +Z with its right on +X and its top on +Y
 * (see `conventions.ts`). A glTF that follows its spec — up +Y, front on +Z,
 * which Babylon's loader keeps on +Z — is already there. Assets converted
 * before the converter followed the spec are not: their forward is on glTF +X,
 * and with `z_up` their up stays on glTF +Z. This records which, so the loader
 * can turn the asset into the standard instead of every layout correcting it.
 */
export interface AssetFrame {
  assetUp: "Y" | "Z";
  assetForward: "X" | "Z";
}

/** The `asset.extras` key every conversion writes its frame stamp under. */
const STAMP_KEY = "roboto";

/** The vendor extension an articulated conversion also records its frame in. */
const DEVICE_EXTENSION = "ROBOTO_device";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function frameFrom(raw: unknown): AssetFrame | undefined {
  if (!isRecord(raw)) {
    return undefined;
  }
  return {
    assetUp: raw.assetUp === "Z" ? "Z" : "Y",
    // A frame block written before the converter followed the glTF spec
    // records no forward: those assets all face glTF +X.
    assetForward: raw.assetForward === "Z" ? "Z" : "X",
  };
}

/**
 * The frame a glTF document records for itself, or undefined when it records
 * none.
 *
 * Every conversion stamps `asset.extras.roboto.frame`. An articulated model
 * converted before that stamp existed still carries its `ROBOTO_device`
 * extension's `frame` block, which records the up axis but not the forward.
 */
export function readAssetFrame(
  document: Record<string, unknown> | undefined,
): AssetFrame | undefined {
  const asset = document?.asset;
  const extras = isRecord(asset) ? asset.extras : undefined;
  const stamp = isRecord(extras) ? extras[STAMP_KEY] : undefined;
  if (isRecord(stamp) && isRecord(stamp.frame)) {
    return frameFrom(stamp.frame);
  }
  const extensions = document?.extensions;
  const device = isRecord(extensions)
    ? extensions[DEVICE_EXTENSION]
    : undefined;
  return isRecord(device) ? frameFrom(device.frame) : undefined;
}

/**
 * Whether a source's status block describes the asset at `assetPath`: a
 * completed conversion of the source's current upload, written to that path.
 *
 * Only then are its settings the ones that asset was converted with. The worker
 * rewrites the whole block, settings included, as a run starts and when it
 * fails, while the asset at the path stays whatever an earlier run left; and a
 * block naming another upload or path describes another asset. The worker's
 * `VisualizationAssetStatus.describes_asset` is the same rule, so the panel
 * and an in-place upgrade agree on which assets' settings are known.
 */
function describesAsset(
  block: VisualizationAssetRecord | null,
  source: Pick<FileRecord, "upload_id">,
  assetPath: string,
): boolean {
  return (
    block?.status === "completed" &&
    block.source_upload_id === source.upload_id &&
    block.relative_path === assetPath
  );
}

/**
 * The frame an asset is in, reading the asset first and its source second.
 *
 * An asset the platform converted before it stamped its frame — a scan, or a
 * model converted with articulation off — carries no record of it, but its
 * source file may: the asset's own path names that file, and its
 * `roboto/visualization_asset` block keeps the `z_up` setting of the run it
 * describes. That is read only when the block describes this asset (see
 * {@link describesAsset}). Otherwise, and when the source can no longer be
 * read, the asset is taken to have used the converter's default. A glTF the
 * platform did not convert has no frame to correct.
 */
export async function assetFrameFor(
  document: Record<string, unknown> | undefined,
  record: Pick<FileRecord, "relative_path">,
  files: Pick<ModelFileService, "getFileRecord">,
  signal: AbortSignal,
): Promise<AssetFrame | undefined> {
  const recorded = readAssetFrame(document);
  if (recorded) {
    return recorded;
  }
  const sourceFileId = sourceFileIdOfAsset(record.relative_path);
  if (sourceFileId === undefined) {
    return undefined;
  }
  let zUp = false;
  try {
    const source = await files.getFileRecord(sourceFileId, {
      abortSignal: signal,
    });
    const block = readVisualizationAsset(source);
    zUp =
      describesAsset(block, source, record.relative_path) &&
      block?.settings?.z_up === true;
  } catch {
    signal.throwIfAborted();
  }
  return { assetUp: zUp ? "Z" : "Y", assetForward: "X" };
}

/**
 * Converted before the spec: URDF forward drawn on −X, left on −Z, up on +Y.
 * A quarter turn about +Y takes −X to +Z and −Z to −X.
 */
const FORWARD_X_CORRECTION = babylon.Quaternion.RotationAxis(
  babylon.Axis.Y,
  Math.PI / 2,
);

/**
 * Converted with `z_up`: URDF forward drawn on −X, left on +Y, up on +Z. The
 * rotation taking (−x, y, z) to (−y, z, x) — a third of a turn about
 * (−1, 1, 1) — puts forward on +Z, left on −X and up on +Y.
 */
const Z_UP_CORRECTION = new babylon.Quaternion(-0.5, 0.5, 0.5, 0.5);

/**
 * The rotation that brings an asset in `frame` into the scene standard —
 * facing +Z, right on +X, top on +Y — or undefined when it already is.
 *
 * Applied above Babylon's `__root__`, never on it: that node's rotation and
 * scaling are the glTF handedness conversion, and writing onto it replaces the
 * conversion instead of composing with it.
 */
export function axesCorrectionFor(
  frame: AssetFrame | undefined,
): babylon.Quaternion | undefined {
  if (!frame) {
    return undefined;
  }
  if (frame.assetUp === "Z") {
    return Z_UP_CORRECTION.clone();
  }
  return frame.assetForward === "Z" ? undefined : FORWARD_X_CORRECTION.clone();
}
